import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSitecoreContext } from 'lib/hooks/useSitecoreContext';
import { RecipeResponse } from 'lib/services/models/content-response';
import { pushToDataLayer, DataLayerEvent } from 'components/helpers/Global/Analytics/DataLayer';
import { removeCookie, setCookie, useCookie } from 'lib/utils/cookies';
import { useRouterUtils } from 'lib/utils/router-utils';
import { useOnRouteChange } from 'lib/hooks/useOnRouteChange';
import { useDisabledFeatureFlags } from 'lib/hooks/useDisabledFeatureFlags';
import { useSiteSettings } from 'lib/hooks/useSiteSettings';
import { useSessionSafe } from 'lib/hooks/auth/useSessionSafe';
import { getOneTrustActiveGroups, getOneTrustCookieActiveGroups } from 'lib/utils/onetrust-utils';
import { useSitecorePage } from 'lib/hooks/sitecore/useSitecorePage';
import { CLIENT_PROFILE_COOKIE_NAME, parseClientProfile } from 'lib/utils/client-profile-cookie';

interface UTagFields {
  author?: string | null;
  author_type?: string | null;
  average_rating?: string | number;
  content_id?: string | null;
  content_title?: string | null;
  content_type?: string | null;
  enhanced_content?: string | null;
  esrc_code?: string | null;
  language?: string | null;
  metadata_label?: string[] | null;
  metadata_value?: string[] | null;
  page_name?: string | null;
  page_type?: string | null;
  publish_date?: string | null;
  rating_count?: number | 0;
  record_type?: string | null;
  review_count?: number | 0;
  search_relevancy_score?: string | number;
  seo_modified_date?: string | null;
  update_type?: string | null;
  user_id?: string | null;
  user_status?: string | null;
}

interface UTagRecipeFields {
  ingredient_base?: string[] | null;
  is_member_generated?: boolean;
  number_of_servings?: string | null;
  prep_time?: string | null;
  total_time?: string | null;
  language?: string | null;
}

interface UserIdentity {
  userId: string | null;
  userStatus: 'Anonymous' | 'Known' | 'EmailOnly' | 'Authenticated';
}

const UTag = () => {
  const { contentItem, language, itemPath, contributor } = useSitecoreContext().sitecoreContext;

  const route = useSitecorePage();

  const userIdentity = useUserIdentity();
  const esrc = useESrc();

  const rdpRecipe = contentItem as RecipeResponse;
  const contentType = contentItem?.ContentType || '';

  // Get contributor name for author field (only for Recipe page)
  const authorName =
    contentType.toLowerCase() === 'recipe' ? contributor?.authorName || null : null;

  const alreadySentUTagRef = useRef(false);

  // Reset the guard when the route changes so the next page can fire.
  // Wrapped in useCallback so useOnRouteChange's effects don't re-run on every render.
  const resetUTagGuard = useCallback(() => {
    alreadySentUTagRef.current = false;
  }, []);
  useOnRouteChange(resetUTagGuard);

  useEffect(() => {
    // We're not ready to set the UTag fields yet
    if (!userIdentity || esrc === 'loading' || alreadySentUTagRef.current) {
      return;
    }

    const uTagFields: UTagFields = {
      ...(authorName && { author: authorName }),
      ...(authorName && { author_type: contributor?.isOrganization ? 'Organization' : 'Person' }),
      average_rating: contentItem?.AverageRating?.toFixed(15) ?? 0,
      content_id: contentItem?.ContentId ?? null,
      content_title: contentItem?.Title ?? null,
      content_type: contentType,
      enhanced_content: contentItem?.MetadataIds?.includes('528bb61f-b718-4d1a-8b8f-b49b25acfb80')
        ? 'enhanced'
        : 'not_enhanced',
      esrc_code: esrc || null, // Use value from cookie or query string
      language: language?.split('-')[0] ?? 'en',
      metadata_label: contentItem?.Metadata?.map((item) => item.split(' : ')[0].trim()) ?? null,
      metadata_value:
        contentItem?.Metadata?.map((item) => item.split(' : ')[1]?.trim() ?? '') ?? null,
      page_name: contentItem?.Title ?? null,
      page_type:
        itemPath === '/'
          ? 'Home'
          : route?.templateId === 'search page guid' //TODO: Replace with actual search page template ID
            ? 'Search'
            : contentItem !== null && contentItem !== undefined
              ? 'Content'
              : 'Other',
      publish_date: contentItem?.DisplayDate ?? null,
      rating_count: contentItem?.RatingCount ?? 0,
      record_type: contentItem?.RecordType || null,
      review_count: contentItem?.ReviewCount ?? 0,
      search_relevancy_score: contentItem?.SearchRelevancyRanking?.toFixed(15) ?? 0,
      seo_modified_date: contentItem?.SEOModifiedDate ?? null,
      update_type:
        !contentItem?.SEOModifiedDate ||
        new Date(contentItem?.PublishDate || '').toDateString() ===
          new Date(contentItem?.SEOModifiedDate || '').toDateString()
          ? 'new'
          : 'updated',
      user_id: userIdentity.userId,
      user_status: userIdentity.userStatus,
    };

    let combinedUTagFields = { ...uTagFields };

    if (contentType.toLowerCase() == 'recipe') {
      const uTagRecipeFields: UTagRecipeFields = {
        ingredient_base:
          rdpRecipe?.IngredientGroups?.flatMap((group) =>
            group.Ingredients.map((ingredient) => ingredient.IngredientBase.Name)
          ) ?? [],
        is_member_generated: false, //TODO hardocoded to false
        number_of_servings: rdpRecipe?.ServingNumber ?? null,
        prep_time: rdpRecipe?.PrepTime ?? null,
        total_time: rdpRecipe?.TotalTime ?? null,
      };
      combinedUTagFields = {
        ...combinedUTagFields,
        ...uTagRecipeFields,
      };
    }
    window.utag_data = combinedUTagFields;
    pushToDataLayer({ event: 'message', ...combinedUTagFields } as DataLayerEvent);
    alreadySentUTagRef.current = true;
  }, [
    authorName,
    contentItem,
    contentType,
    contributor?.isOrganization,
    esrc,
    itemPath,
    language,
    rdpRecipe?.IngredientGroups,
    rdpRecipe?.PrepTime,
    rdpRecipe?.ServingNumber,
    rdpRecipe?.TotalTime,
    route?.templateId,
    userIdentity,
  ]);
  return <></>;
};

export default UTag;

function useUserIdentity(): UserIdentity | null {
  const { data: session, status } = useSessionSafe();
  const clientProfileCookie = useCookie(CLIENT_PROFILE_COOKIE_NAME);

  return resolveUserIdentity(status, session?.user?.id, clientProfileCookie);
}

export function resolveUserIdentity(
  sessionStatus: 'authenticated' | 'loading' | 'unauthenticated' | undefined,
  sessionUserId: string | null | undefined,
  clientProfileCookie: string | null | 'loading'
): UserIdentity | null {
  if (sessionStatus === 'loading' || clientProfileCookie === 'loading') {
    return null;
  }

  if (sessionStatus === 'authenticated') {
    return {
      userId: sessionUserId ?? null,
      userStatus: 'Authenticated',
    };
  }

  const clientProfile = parseClientProfile(clientProfileCookie);
  if (clientProfile) {
    return {
      userId: clientProfile.userId,
      userStatus: clientProfile.profileType === 'User' ? 'Known' : 'EmailOnly',
    };
  }

  return {
    userId: null,
    userStatus: 'Anonymous',
  };
}

function useESrc() {
  // Read esrc from cookie
  const esrcCookie = useCookie('gmi.profile.esrc');
  const disabledFeatures = useDisabledFeatureFlags();
  const siteSettings = useSiteSettings();
  const [esrc, setESrc] = useState<string | null | 'loading'>('loading');
  const { removeQueryParameter, isReady, getQueryParameter } = useRouterUtils();
  const esrcParam = getQueryParameter('esrc') as string | undefined;

  // esrcCode from site settings acts as a fallback when no URL param or cookie is present
  const esrcFromSiteSettings =
    (siteSettings as Record<string, { value?: string } | undefined> | null)?.esrcCode?.value ??
    null;

  useEffect(() => {
    // Wait until cookies and router are ready
    if (esrcCookie === 'loading' || !isReady) {
      return;
    }

    // When OneTrust is enabled, read OptanonConsent directly from document.cookie.
    // OneTrustScript already reloads the page on consent change, so a direct read is sufficient.
    // Allow by default when OT is disabled or the consent cookie is not yet set.
    const oneTrustEnabled = !disabledFeatures?.OT;
    const hasTargetingConsent = !oneTrustEnabled || isOneTrustGroupActive('4');

    if (!hasTargetingConsent) {
      // Targeting consent is off — remove the cookie and clear state
      setESrc(null);
      removeCookie('gmi.profile.esrc');
      return;
    }

    if (esrcParam) {
      // URL param takes priority — set state, persist to cookie, and remove from URL
      setESrc(esrcParam);
      setCookie('gmi.profile.esrc', esrcParam);
      removeQueryParameter('esrc');
    } else if (esrcCookie) {
      // No URL param but cookie exists — use the stored cookie value
      setESrc(esrcCookie);
    } else if (esrcFromSiteSettings) {
      // No URL param and no cookie — fall back to site settings esrcCode
      setCookie('gmi.profile.esrc', esrcFromSiteSettings);
      setESrc(esrcFromSiteSettings);
    } else {
      // No source available — remove any stale cookie and clear state
      setESrc(null);
      removeCookie('gmi.profile.esrc');
    }
  }, [
    esrcCookie,
    esrcParam,
    esrcFromSiteSettings,
    removeQueryParameter,
    isReady,
    disabledFeatures,
  ]);

  if (esrc === 'loading') {
    return 'loading';
  }
  return esrc;
}

/**
 * Checks whether a specific OneTrust consent group is opted-in.
 * Reads the raw (URL-encoded) OptanonConsent cookie directly from document.cookie,
 * matching the same parsing logic used in OneTrustFilter.
 * @param group - The group number to check, e.g. '4' for Targeting cookies
 * @returns true if the group is active, or true by default when consent status is unknown
 */
function isOneTrustGroupActive(group: string): boolean {
  if (typeof document === 'undefined') return true;

  const activeGroups = getOneTrustCookieActiveGroups();

  if (activeGroups) {
    return activeGroups.includes(group);
  }

  // No consent cookie yet — honor OneTrust's admin-configured default toggle state
  // for this category (sliders on/off) instead of assuming consent is granted.
  const defaultActiveGroups = getOneTrustActiveGroups();
  if (defaultActiveGroups.length > 0) {
    return defaultActiveGroups.includes(group);
  }

  return true; // OneTrust SDK not yet available — allow by default
}
